/* eslint-disable react/react-in-jsx-scope */
import {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useContext,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AppContext } from "../../AppContext";

import ReportToolbar from "./report/ReportToolbar";
import NewReportToolbar from "./report/NewReportToolbar";
import PageNotAccessible from "../PageNotAccessible";
import DataLoading from "./../DataLoading";

import DashboardWebApi from "./../../services/DashboardWebApi";
import {
  PowerBiReport,
  PowerBiDataset,
  ActivityLogEntry,
} from "../../models/models";

import * as powerbi from "powerbi-client";
import * as models from "powerbi-models";

// ensure Power BI JavaScript API has fully loaded to avoid race-condition
require("powerbi-models");
require("powerbi-client");

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";

export type ViewMode = "FitToPage" | "FitToWidth" | "ActualSize" | "";

const Report = () => {
  const embedContainer = useRef(null);
  const isAuthenticated = DashboardWebApi.IsAuthenticated();
  const user = DashboardWebApi.GetUser();

  const navigate = useNavigate();
  const { id } = useParams();

  const { embeddingData, refreshEmbeddingData } = useContext(AppContext);

  const [embedToken, setEmbedToken] = useState<string>(null);
  const [embedTokenExpiration, setEmbedTokenExpiration] =
    useState<string>(null);
  const [embedTokenAcquired, setEmbedTokenAcquired] = useState<boolean>(false);
  // Embed token expiration status
  const [embedTokenExpirationDisplay, setEmbedTokenExpirationDisplay] =
    useState<string>("");

  const [embeddedReport, setEmbeddedReport] = useState<powerbi.Report | null>(
    null,
  );
  const [embeddedNewReport, setEmbeddedNewReport] =
    useState<powerbi.Embed | null>(null);

  const [embedType, setEmbedType] = useState<
    "ExistingReport" | "NewReport" | null
  >(null);
  const [reportType, setReportType] = useState<
    "PowerBiReport" | "PaginatedReport" | null
  >(null);
  const [viewMode, setViewMode] = useState<ViewMode>("FitToWidth");
  const [editMode, setEditMode] = useState(false);
  const [showNavigation, setShowNavigation] = useState(true);
  const [showFiltersPane, setShowFiltersPane] = useState(true);
  const [showBookmarksPane, setShowBookmarksPane] = useState(false);
  const [pageNavigationAvailable, setPageNavigationAvailable] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportLoaded, setReportLoaded] = useState(false);

  const embeddedReportRef = useRef(null);

  const embedExistingReport = async (Report: PowerBiReport) => {
    const logHeader = `[ExistingReport/${Report?.name}]`;
    console.debug(`${logHeader} Displaying report ${Report.name}`, {Report});

    setEmbedType("ExistingReport");
    setReportType("PowerBiReport");

    const params = new URLSearchParams(globalThis.location.search);
    const openInEditMode = params.get("edit") === "true";
    setEditMode(openInEditMode);

    const defaultShowNavigation: boolean = true;
    setShowNavigation(defaultShowNavigation);
    const defaultShowFilterPane: boolean = false;
    setShowFiltersPane(defaultShowFilterPane);
    const defaultShowBookmarksPane: boolean = false;
    setShowBookmarksPane(defaultShowBookmarksPane);
    const defaultViewMode = "FitToWidth";
    setViewMode(defaultViewMode);

    // determine permissions for embedded report
    let permissions;
    if (embeddingData.userCanEdit && embeddingData.userCanCreate) {
      permissions = models.Permissions.All;
    } else if (embeddingData && !embeddingData.userCanCreate) {
      permissions = models.Permissions.ReadWrite;
    } else if (!embeddingData.userCanEdit && embeddingData.userCanCreate) {
      permissions = models.Permissions.Copy;
    } else if (!embeddingData.userCanEdit && !embeddingData.userCanCreate) {
      permissions = models.Permissions.Read;
    }

    const config: powerbi.IReportEmbedConfiguration = {
      type: "report",
      id: Report.id,
      embedUrl: Report.embedUrl,
      accessToken: embedToken,
      tokenType: models.TokenType.Embed,
      viewMode: openInEditMode ? models.ViewMode.Edit : models.ViewMode.View,
      permissions: permissions,
      settings: {
        bars: {
          actionBar: { visible: false },
        },
        panes: {
          pageNavigation: {
            visible: defaultShowNavigation,
            position: models.PageNavigationPosition.Left,
          },
          filters: { visible: defaultShowFilterPane, expanded: false },
          bookmarks: { visible: defaultShowBookmarksPane },
        },
      },
    };

    // set up variables to collect performance data for report loading
    let timerStart: number = Date.now();
    let initialLoadComplete: boolean = false;
    let pageChangeInProgress: boolean = false;
    let loadDuration: number;
    let renderDuration: number;

    const existingReport = embeddedReportRef.current;
    if (existingReport) {
      console.debug(
        `${logHeader} Found an existing embedded report `,
        existingReport.config.uniqueId);
      removeEmbeddedReportHandlers(existingReport);
    } else {
      console.debug(
        `${logHeader} There is no existing embedded report`);    
    }
    
    // Embed the report and display it within the div container
    setReportLoading(true);
    setReportLoaded(false);
    globalThis.powerbi.reset(embedContainer.current);
    const embeddedReport: powerbi.Report = globalThis.powerbi.embed(
      embedContainer.current,
      config,
    ) as powerbi.Report;

    console.debug(`${logHeader} Created embedded report `,
                  embeddedReport.config.uniqueId);

    setEmbeddedReport(embeddedReport);
    embeddedReportRef.current = embeddedReport;
    setEmbeddedNewReport(null);

    embeddedReport.off("loaded");
    embeddedReport.on("loaded", async (event: CustomEvent) => {
      loadDuration = Date.now() - timerStart;
    });

    embeddedReport.off("rendered");
    embeddedReport.on("rendered", async (event: CustomEvent) => {
      const logHeader =
        `[ExistingReport/rendered/${embeddedReport.config.uniqueId}]`;
      console.debug(`${logHeader} Inside rendered handler`, {event});
      console.debug(`${logHeader} Report =`, {Report});
      console.debug(`${logHeader} embeddedReport =`, {embeddedReport});

      renderDuration = Date.now() - timerStart;

      let correlationId: string = "unknown";
      try {
        correlationId = await embeddedReport.getCorrelationId();
      } catch(err) {
        console.debug(`${logHeader} Unable to get correlation id.`, {err})
      }
      
      let pageName: string = "unknown";
      if (Report.reportType === "PowerBIReport") {
         try {
           pageName = (await embeddedReport.getActivePage()).displayName;
         } catch(err) {
           console.debug(`${logHeader} Unable to get page name.`, {err})
         }
      }        

      console.debug(`${logHeader} pageName = ${pageName}, correlationId = ${correlationId}`);
      
      if (initialLoadComplete) {
        if (pageChangeInProgress) {
          console.debug(`${logHeader} Page change complete`);
          pageChangeInProgress = false;
          await logPageChangedActivity(
            correlationId,
            Report,
            pageName,
            renderDuration
          );
        } else {
          console.debug(`${logHeader} No page change in progress`);
        }
      } else {
        console.debug(`${logHeader} First page load complete`);
        initialLoadComplete = true;
        setReportLoading(false);
        setReportLoaded(true);

        // Check if page navigation is available
        // Page navigation is only useful for multi-page reports
        try {
          const pages = await embeddedReport.getPages();
          console.debug(`${logHeader} Report has ${pages.length} page(s)`);
          if (pages.length <= 1) {
            console.debug(`${logHeader} Hiding page navigation toggle (single page)`);
            setPageNavigationAvailable(false);
          } else {
            console.debug(`${logHeader} Showing page navigation toggle (multi-page report)`);
            setPageNavigationAvailable(true);
          }
        } catch (err) {
          console.error(`${logHeader} Error checking page count:`, err);
          setPageNavigationAvailable(true); // Show toggle by default on error
        }

        await logViewReportActivity(
          correlationId,
          Report,
          pageName,
          loadDuration,
          renderDuration,
        );
      }
    });

    embeddedReport.off("pageChanged");
    embeddedReport.on("pageChanged", async (event: CustomEvent) => {
      console.debug(`${logHeader} Inside pageChanged handler`, {event});
      if (initialLoadComplete) {
        pageChangeInProgress = true;
        timerStart = Date.now();

        const currentViewMode = sessionStorage.getItem("viewMode");
        if (currentViewMode === "FitToPage") {
          embeddedReport.updateSettings({
            layoutType: models.LayoutType.Custom,
            customLayout: { displayOption: models.DisplayOption.FitToPage },
          });
          setViewMode("FitToPage");
        } else if (currentViewMode === "ActualSize") {
          embeddedReport.updateSettings({
            layoutType: models.LayoutType.Custom,
            customLayout: { displayOption: models.DisplayOption.ActualSize },
          });
          setViewMode("ActualSize");
        } else {
          embeddedReport.updateSettings({
            layoutType: models.LayoutType.Custom,
            customLayout: { displayOption: models.DisplayOption.FitToWidth },
          });
          setViewMode("FitToWidth");
        }
      }
    });

    embeddedReport.off("saved");
    console.log(`${logHeader} [EVENT-HANDLER] Attaching 'saved' event handler to report ${embeddedReport.config.uniqueId}`);
    embeddedReport.on("saved", async (event: CustomEvent) => {
      try {
        // Clear the heartbeat and timeout if they exist
        if ((globalThis as any).__saveAsHeartbeat) {
          clearInterval((globalThis as any).__saveAsHeartbeat);
          (globalThis as any).__saveAsHeartbeat = null;
        }
        if ((globalThis as any).__saveAsTimeout) {
          clearTimeout((globalThis as any).__saveAsTimeout);
          (globalThis as any).__saveAsTimeout = null;
        }

        // Clear the isSaved tracking state
        if ((globalThis as any).__saveAsPreviouslySaved !== undefined) {
          (globalThis as any).__saveAsPreviouslySaved = undefined;
        }

        // Log timing if we have context
        if ((globalThis as any).__saveAsContext) {
          const elapsed = Date.now() - (globalThis as any).__saveAsContext.startTime;
          console.log(`${logHeader} [SAVED-EVENT-FIRED] Event fired ${Math.round(elapsed / 1000)}s after saveAs() call`);
        }

        console.log(`${logHeader} [SAVED-EVENT-FIRED] Saved event received at ${new Date().toISOString()}`);
        console.log(`${logHeader} [SAVED-EVENT-FIRED] event.detail:`, event.detail);
        console.log(`${logHeader} [SAVED-EVENT-FIRED] event.detail.saveAs: ${event.detail.saveAs}`);

        if (event.detail.saveAs) {
          // handle save-as with newly created report
          const startTime = Date.now();
          console.log(`${logHeader} [SAVEAS-START] Processing SaveAs event at ${new Date().toISOString()}`);
          console.log(`${logHeader} [SAVEAS-DETAIL]`, event.detail);

          console.log(`${logHeader} [SAVEAS-STEP-1] Refreshing embed token...`);
          await refreshEmbedToken();
          console.log(`${logHeader} [SAVEAS-STEP-1] ✓ Embed token refreshed (${Date.now() - startTime}ms)`);

          console.log(`${logHeader} [SAVEAS-STEP-2] Refreshing embedding data...`);
          const refreshStart = Date.now();
          await refreshEmbeddingData();
          console.log(`${logHeader} [SAVEAS-STEP-2] ✓ Embedding data refreshed (${Date.now() - refreshStart}ms)`);

          const newReportId = event.detail.reportObjectId;
          const newReportName = event.detail.reportName;
          console.log(`${logHeader} [SAVEAS-STEP-3] New report: ${newReportName} (${newReportId})`);

          logCopyReportActivity(Report, newReportId, newReportName);

          console.log(`${logHeader} [SAVEAS-STEP-4] Navigating to /reports/${newReportId}/?edit=true`);
          navigate(`/reports/${newReportId}/?edit=true`);
          console.log(`${logHeader} [SAVEAS-END] ✓ SaveAs complete (total: ${Date.now() - startTime}ms)`);
        } else {
          // handle save to to edit exisitng report
          console.log(`${logHeader} [SAVED-EVENT-FIRED] Processing regular Save (not SaveAs)`);
          logEditReportActivity(Report);
        }
      } catch (error) {
        console.error(`${logHeader} [SAVEAS-ERROR] Error in saved handler:`, error);
        throw error;
      }
    });
    console.log(`${logHeader} [EVENT-HANDLER] ✓ 'saved' event handler attached`);

    embeddedReport.off("error");
    embeddedReport.on("error", (event: CustomEvent) => {
      console.error(`${logHeader} [POWERBI-ERROR] Power BI error event received:`, event);
      console.error(`${logHeader} [POWERBI-ERROR] Error detail:`, event.detail);

      // Clear heartbeat on error
      if ((globalThis as any).__saveAsHeartbeat) {
        clearInterval((globalThis as any).__saveAsHeartbeat);
        (globalThis as any).__saveAsHeartbeat = null;
        console.error(`${logHeader} [POWERBI-ERROR] Cleared Save As heartbeat due to error`);
      }

      // Clear the isSaved tracking state on error
      if ((globalThis as any).__saveAsPreviouslySaved !== undefined) {
        (globalThis as any).__saveAsPreviouslySaved = undefined;
        console.error(`${logHeader} [POWERBI-ERROR] Cleared Save As isSaved tracking state due to error`);
      }
    });
  };

  const embedPaginatedReport = async (Report: PowerBiReport) => {
    const logHeader = `[PaginatedReport/${Report?.name}]`;
    console.debug(`${logHeader} Displaying report ${Report.name}`, {Report});
    setReportLoading(true);
    setReportLoaded(false);
    globalThis.powerbi.reset(embedContainer.current);

    setEmbedType("ExistingReport");
    setReportType("PaginatedReport");

    const config: models.IPaginatedReportLoadConfiguration = {
      type: "report",
      id: Report.id,
      embedUrl: Report.embedUrl,
      accessToken: embedToken,
      tokenType: models.TokenType.Embed,
      settings: {
        commands: {
          parameterPanel: {
            enabled: true,
            expanded: true,
          },
        },
      },
    };

    const embeddedReport: powerbi.Report = globalThis.powerbi.embed(
      embedContainer.current,
      config,
    ) as powerbi.Report;

    setEmbeddedReport(embeddedReport);
    embeddedReportRef.current = embeddedReport;
    setEmbeddedNewReport(null);

    await logViewReportActivity("", Report, "", undefined, undefined);

    embeddedReport.off("loaded");
    embeddedReport.on("loaded", async (event: CustomEvent) => {
      setReportLoading(false);
      setReportLoaded(true);
    });

    embeddedReport.off("error");
    embeddedReport.on("error", (event: CustomEvent) => {
      console.error(`[PaginatedReport/${embeddedReport.config.uniqueId}/error]`,
                    {event});
      setReportLoading(false);
    });
  };

  const embedNewReport = async (Dataset: PowerBiDataset) => {
    console.debug("[NewReport] Creating new report", {Dataset});
    setReportLoading(true);
    setReportLoaded(false);
    setEmbedType("NewReport");
    setReportType("PowerBiReport");

    const config: powerbi.IEmbedConfiguration = {
      type: "report",
      datasetId: Dataset.id,
      embedUrl: Dataset.createReportEmbedURL,
      accessToken: embedToken,
      tokenType: models.TokenType.Embed,
      settings: {
        bars: {
          actionBar: { visible: false },
        },
        panes: {
          filters: { expanded: false, visible: true },
        },
      },
    };

    // Embed the report and display it within the div container.
    const embeddedNewReport: powerbi.Embed = globalThis.powerbi.createReport(
      embedContainer.current,
      config,
    );

    setEmbeddedNewReport(embeddedNewReport);
    if (embeddedReportRef.current) {
      removeEmbeddedReportHandlers(embeddedReportRef.current);
    }
    setEmbeddedReport(null);
    embeddedReportRef.current = null;

    embeddedNewReport.off("loaded");
    embeddedNewReport.on("loaded", async (event: CustomEvent) => {
      setReportLoading(false);
      setReportLoaded(true);
    });

    embeddedNewReport.off("saved");
    embeddedNewReport.on("saved", async (event: CustomEvent) => {
      console.debug("Processing Saved event");
      await refreshEmbedToken();
      console.debug("Refreshing embedding data");
      await refreshEmbeddingData();
      console.debug("Refreshed embedding data");
      // get ID and name of new report
      const newReportId = event.detail.reportObjectId;
      const newReportName = event.detail.reportName;
      logCreateReportActivity(Dataset, newReportId, newReportName);
      console.debug(`Navigating to new report ${newReportId}, ${newReportName}`);
      navigate(`/reports/${newReportId}/?edit=true&newReport=true`);
    });

    embeddedNewReport.off("error");
    embeddedNewReport.on("error", (event: CustomEvent) => {
      console.error(`[NewReport/${embeddedNewReport.config.uniqueId}/error]`,
                    {event});
    });
  };

  // set height of embed container relative to height of window
  const setReportContainerHeight = () => {
    if (embedContainer.current) {
      const reportContainer: HTMLElement = embedContainer.current;
      const reportContainerTopPosition =
        reportType === "PaginatedReport" ? 76 : 109;
      reportContainer.style.height =
        globalThis.innerHeight - reportContainerTopPosition + "px";
    }
  };

  const logViewReportActivity = async (
    correlationId: string,
    report: PowerBiReport,
    pageName: string,
    loadDuration: number,
    renderDuration: number,
  ) => {
    const logEntry: ActivityLogEntry = new ActivityLogEntry();
    logEntry.CorrelationId = correlationId;
    logEntry.Activity = "ViewReport";
    logEntry.LoginId = user.LoginId;
    logEntry.User = user.UserName;
    logEntry.Tenant = embeddingData.tenantName;
    logEntry.Report = report.name;
    logEntry.ReportId = report.id;
    logEntry.ReportType = report.reportType;
    logEntry.PageName = pageName;
    logEntry.DatasetId = report.datasetId;
    logEntry.Dataset = embeddingData.datasets.find(
      (dataset) => dataset.id === report.datasetId,
    )?.name;
    logEntry.LoadDuration = loadDuration;
    logEntry.RenderDuration = renderDuration;
    await DashboardWebApi.LogActivity(logEntry);
  };

  const logPageChangedActivity = async (
    correlationId: string,
    report: PowerBiReport,
    pageName: string,
    renderDuration: number,
  ) => {
    const logEntry: ActivityLogEntry = new ActivityLogEntry();
    logEntry.CorrelationId = correlationId;
    logEntry.Activity = "PageChanged";
    logEntry.LoginId = user.LoginId;
    logEntry.User = user.UserName;
    logEntry.Tenant = embeddingData.tenantName;
    logEntry.Report = report.name;
    logEntry.ReportId = report.id;
    logEntry.ReportType = report.reportType;
    logEntry.PageName = pageName;
    logEntry.DatasetId = report.datasetId;
    logEntry.Dataset = embeddingData.datasets.find(
      (dataset) => dataset.id === report.datasetId,
    )?.name;
    logEntry.RenderDuration = renderDuration;
    await DashboardWebApi.LogActivity(logEntry);
  };

  const logEditReportActivity = async (report: PowerBiReport) => {
    const logEntry: ActivityLogEntry = new ActivityLogEntry();
    logEntry.CorrelationId = "";
    logEntry.Activity = "EditReport";
    logEntry.LoginId = user.LoginId;
    logEntry.User = user.UserName;
    logEntry.Tenant = embeddingData.tenantName;
    logEntry.Report = report.name;
    logEntry.ReportId = report.id;
    logEntry.ReportType = "PowerBIReport";
    logEntry.DatasetId = report.datasetId;
    logEntry.Dataset = embeddingData.datasets.find(
      (dataset) => dataset.id === report.datasetId,
    ).name;
    await DashboardWebApi.LogActivity(logEntry);
  };

  const logCopyReportActivity = async (
    orginalReport: PowerBiReport,
    reportId: string,
    reportName: string,
  ) => {
    const logEntry: ActivityLogEntry = new ActivityLogEntry();
    logEntry.Activity = "CopyReport";
    logEntry.LoginId = user.LoginId;
    logEntry.User = user.UserName;
    logEntry.Tenant = embeddingData.tenantName;
    logEntry.Report = reportName;
    logEntry.ReportId = reportId;
    logEntry.ReportType = "PowerBIReport";
    logEntry.OriginalReportId = orginalReport.id;
    logEntry.DatasetId = orginalReport.datasetId;
    logEntry.Dataset = embeddingData.datasets.find(
      (dataset) => dataset.id === orginalReport.datasetId,
    ).name;
    await DashboardWebApi.LogActivity(logEntry);
  };

  const logCreateReportActivity = async (
    dataset: PowerBiDataset,
    reportId: string,
    reportName: string,
  ) => {
    const logEntry: ActivityLogEntry = new ActivityLogEntry();
    logEntry.Activity = "CreateReport";
    logEntry.LoginId = user.LoginId;
    logEntry.User = user.UserName;
    logEntry.Tenant = embeddingData.tenantName;
    logEntry.Report = reportName;
    logEntry.ReportId = reportId;
    logEntry.ReportType = "PowerBIReport";
    logEntry.DatasetId = dataset.id;
    logEntry.Dataset = dataset.name;
    await DashboardWebApi.LogActivity(logEntry);
  };

  const removeEmbeddedReportHandlers = (report: powerbi.Report) => {
    console.debug("[Report] Renoving handlers from embedded report %s",
                  report.config.uniqueId);
    report.off("loaded");
    report.off("rendered");
    report.off("pageChanged");
    report.off("saved");
    report.off("error");
  }
  
  const getEmbedToken = async () => {
    console.debug("[Report] Getting embed token");
    const tokenResult = await DashboardWebApi.GetEmbedToken();
    setEmbedToken(tokenResult.embedToken);
    setEmbedTokenExpiration(tokenResult.embedTokenExpiration);
    setEmbedTokenAcquired(true);
    monitorTokenExpiration(tokenResult.embedTokenExpiration);
  };

  const refreshEmbedToken = async () => {
    console.debug("[Report] Refreshing embed token");
    const tokenResult = await DashboardWebApi.GetEmbedToken();
    setEmbedToken(tokenResult.embedToken);
    setEmbedTokenExpiration(tokenResult.embedTokenExpiration);
    setEmbedTokenExpirationDisplay("Refreshing embed token");
    monitorTokenExpiration(tokenResult.embedTokenExpiration);

    if (embeddedReport) {
      embeddedReport.setAccessToken(tokenResult.embedToken);
    }

    if (embeddedNewReport) {
      embeddedNewReport.setAccessToken(tokenResult.embedToken);
    }
  };

  const monitorTokenExpiration = (EmbedTokenExpiration: string): void => {
    const secondsToExpire = Math.floor(
      (new Date(EmbedTokenExpiration).getTime() - Date.now()) / 1000,
    );

    // auto-refresh embed token 2 minutes before it expires
    const secondsBeforeExpirationForAutoRefresh = 2 * 60;
    if (secondsToExpire < secondsBeforeExpirationForAutoRefresh) {
      refreshEmbedToken();
    } else {
      const minutes = Math.floor(secondsToExpire / 60);
      const seconds = secondsToExpire % 60;
      let timeToExpire = `Token Expiration: ${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      if (timeToExpire === embedTokenExpirationDisplay) {
        timeToExpire += " ";
      }
      setEmbedTokenExpirationDisplay(timeToExpire);
    }
  };

  // set height of embed container DIV
  useLayoutEffect(() => {
    if (isAuthenticated && embedContainer.current) {
      setReportContainerHeight();
      globalThis.addEventListener("resize", setReportContainerHeight);
    }
  }, [isAuthenticated, reportType, embedContainer]);

  // call Web API to retreive embed token and embed report
  useEffect(() => {
    if (
      !isAuthenticated ||
      !embedContainer.current ||
      embeddingData.tenantName == null
    ) {
      return;
    }

    if (!embedTokenAcquired) {
      // get embed token for the first time
      getEmbedToken();
      return;
    }

    // embed existing report if id match id from URL
    const report: PowerBiReport = embeddingData.reports?.find(
      (report) => report.id === id,
    );
    if (report) {
      if (report.reportType === "PowerBIReport") {
        embedExistingReport(report);
      } else {
        embedPaginatedReport(report);
      }
    } else {
      // embed new report using this dataset if id matches id from URL
      const dataset: PowerBiDataset = embeddingData.datasets?.find(
        (dataset) => dataset.id === id,
      );
      if (dataset) {
        embedNewReport(dataset);
      }
    }
  }, [
    isAuthenticated,
    embeddingData,
    embedTokenAcquired,
    embedContainer.current,
    id,
  ]);

  // set up repeating effect to update display for embed token expiration time
  useEffect(() => {
    if (isAuthenticated && embedTokenAcquired) {
      globalThis.setTimeout(() => {
        monitorTokenExpiration(embedTokenExpiration);
      }, 1000);
    }
  }, [
    isAuthenticated,
    embedTokenAcquired,
    embedTokenExpiration,
    embedTokenExpirationDisplay,
  ]);

  // Expose Power BI state for testing (test mode only)
  useEffect(() => {
    if (embeddedReport && process.env.NODE_ENV === 'test') {
      (globalThis as any).__powerbi_state__ = {
        filterPaneVisible: showFiltersPane,
        navigationPaneVisible: showNavigation,
        bookmarksPaneVisible: showBookmarksPane,
        viewMode: viewMode,
        editMode: editMode,
        reportLoaded: reportLoaded,
        reportLoading: reportLoading,
        reportId: id,
        embedType: embedType,
        reportType: reportType,
      };
    }
  }, [
    embeddedReport,
    showFiltersPane,
    showNavigation,
    showBookmarksPane,
    viewMode,
    editMode,
    reportLoaded,
    reportLoading,
    id,
    embedType,
    reportType,
  ]);

  if (!isAuthenticated) {
    return <PageNotAccessible />;
  } else if (
    isAuthenticated &&
    embeddingData.user &&
    !embeddingData.tenantName
  ) {
    return (
      <Container maxWidth={false}>
        <Alert
          sx={{ border: 1, padding: 2, mx: 2, marginTop: 5 }}
          severity="warning"
        >
          Your user account has not been assigned to a tenant. You will not have
          access to any reports until your user account has been assigned to a
          tenant.
        </Alert>
      </Container>
    );
  } else if (embeddingData.workspaceArtifactsLoading) {
    return <DataLoading />;
  } else {
    return (
      <Box
        sx={{ display: "grid", gridAutoFlow: "row", width: 1 }}
        data-testid="report-page"
        data-report-loading={reportLoading}
        data-report-loaded={reportLoaded}
        data-report-id={id}
      >
        {embedType === "ExistingReport" && reportType === "PowerBiReport" && (
          <ReportToolbar
            report={embeddedReport}
            editMode={editMode}
            setEditMode={setEditMode}
            showNavigation={showNavigation}
            setShowNavigation={setShowNavigation}
            showFiltersPane={showFiltersPane}
            setShowFiltersPane={setShowFiltersPane}
            viewMode={viewMode}
            setViewMode={setViewMode}
            showBookmarksPane={showBookmarksPane}
            setShowBookmarksPane={setShowBookmarksPane}
            setEmbedToken={setEmbedToken}
            setEmbedTokenExpiration={setEmbedTokenExpiration}
            embedTokenExpiration={embedTokenExpiration}
            embedTokenExpirationDisplay={embedTokenExpirationDisplay}
            pageNavigationAvailable={pageNavigationAvailable}
          />
        )}

        {embedType === "NewReport" && (
          <NewReportToolbar report={embeddedNewReport} />
        )}

        {reportLoading && (
          <DataLoading data-testid="report-loading-spinner" />
        )}

        <Box
          ref={embedContainer}
          data-testid="powerbi-embed-container"
          sx={{ width: "100%", borderBottom: 1, borderBottomColor: "#24125F" }}
        />
      </Box>
    );
  }
};

export default Report;
